<template>
  <div :class="bem.b('wrapper')" v-bind="containerAttrs">
    <slot name="header" />
    <a-tree
      v-bind="componentAttrs"
      v-model:selected-keys="newSelectedKeys"
      v-model:checked-keys="newCheckedKeys"
      :expanded-keys="
        !expandedKeys && defaultExpandAll ? undefined : newExpandedKeys
      "
      :tree-data="treeData"
      :class="[bem.b(), bem.is(height && virtual ? 'virtual' : '')]"
      :default-expand-all="defaultExpandAll"
      :multiple="multiple"
      :field-names="fieldNames"
      :virtual="virtual"
      :height="height"
      :check-strictly="checkStrictly"
      :load-data="enableLazyLoad ? loadData : undefined"
      :loaded-keys="cacheLoadedData ? loadedKeys : []"
      :show-line="showLine && { showLeafIcon }"
      @update:expanded-keys="(v) => (newExpandedKeys = v)"
      @expand="onExpand"
      @select="onSelect"
      @check="onCheck"
    >
      <template v-for="item in Object.keys(filterSlots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>

      <!-- 展开收起图标 -->
      <template #switcherIcon="data">
        <slot name="switcherIcon" v-bind="data">
          <!-- 展开状态图标 -->
          <ll-icon
            v-if="data.expanded"
            :icon-name="expandIcon?.iconName || 'icon-solid-arrow-down'"
            :size="expandIcon?.size || 12"
            :color="expandIcon?.color"
          />
          <!-- 收起状态图标 -->
          <ll-icon
            v-else
            :icon-name="collapseIcon?.iconName || 'icon-solid-arrow-right'"
            :size="collapseIcon?.size || 12"
            :color="collapseIcon?.color"
          />
        </slot>
      </template>

      <!-- 标题title -->
      <template #title="data">
        <slot name="title" v-bind="data">
          <!-- 节点内容 -->
          <div :class="bem.e('node-content')" @click="nodeClick(data)">
            <!-- 图标 -->
            <div
              v-if="showIcon && data.dataRef.icon"
              :class="bem.e('title-icon')"
            >
              <slot name="title-icon" v-bind="data">
                <ll-icon
                  :icon-name="data.dataRef.icon"
                  :size="iconSize"
                  :color="iconColor"
                />
              </slot>
            </div>

            <!-- 显示文本 -->
            <div
              :class="bem.e('title-text')"
              :style="{
                color: textColor,
                fontSize: `${textSize}px`,
              }"
            >
              <slot name="title-text" v-bind="data">
                {{ data.dataRef[fieldTitle] }}
              </slot>
            </div>

            <!-- 操作区 -->
            <div v-if="enableOperate" :class="bem.e('operate')">
              <slot name="operate" v-bind="data" />
            </div>
          </div>
        </slot>
      </template>
    </a-tree>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { TreeProps } from 'ant-design-vue'
import { Key } from 'ant-design-vue/es/_util/type'
import { DataNode } from 'ant-design-vue/es/tree'
import { omit } from 'lodash-es'
import { computed, ref, useAttrs, useSlots } from 'vue'
import { treeEmits, treeProps } from './tree'

defineOptions({ name: 'LlTree', inheritAttrs: false })

const slots = useSlots()
const attrs = useAttrs()

const props = defineProps(treeProps)
const emits = defineEmits(treeEmits)

const bem = createNamespace('tree')

// 容器样式
const containerAttrs = computed(() => ({
  class: attrs.class as any,
  style: attrs.style as any,
}))

// 组件功能相关的属性
const componentAttrs = computed(() => omit(attrs, ['class', 'style']))

// 过滤自定义插槽
const filterSlots = computed(() => {
  return Object.keys(slots).filter(
    (name) => name !== 'switcherIcon' && name !== 'title' && name !== 'header'
  )
})

const fieldKey = computed(() => props.fieldNames?.['key'] || 'key')
const fieldTitle = computed(() => props.fieldNames?.['title'] || 'title')
const fieldChildren = computed(
  () => props.fieldNames?.['children'] || 'children'
)

const initSelectedKeys = ref([] as Key[])
const initExpandedKeys = ref([] as Key[])
const initCheckedKeys = ref([] as TreeProps['checkedKeys'])

// 选中节点数组
const newSelectedKeys = computed({
  get: () => props.selectedKeys || initSelectedKeys.value,
  set(val: Key[]) {
    initSelectedKeys.value = val
    emits('update:selectedKeys', val)
  },
})

// 展开数组
const newExpandedKeys = computed({
  get: () => props.expandedKeys || initExpandedKeys.value,
  set(val: Key[]) {
    initExpandedKeys.value = val
    emits('update:expandedKeys', val)
  },
})

// 复选框数组
const newCheckedKeys = computed({
  get: () => props.checkedKeys || initCheckedKeys.value,
  set: (val: TreeProps['checkedKeys']) => {
    initCheckedKeys.value = val
    emits('update:checkedKeys', val)
  },
})

// ————————————————————————————事件———————————————————————— //

// 展开事件
const onExpand = (keys: Key[], e?: any) => {
  emits('expand', keys, e)
}
// 选中事件
const onSelect = (keys: Key[], e?: any) => {
  emits('select', keys, e)
}
// 选择事件
const onCheck = (keys: TreeProps['checkedKeys'], e?: any) => {
  emits('check', keys, e)
}

// ———————————————————————————内部方法———————————————————————— //

// 点击行
const nodeClick = (data: any) => {
  if (!props.clickRowExpand) return
  const state = newExpandedKeys.value.includes(data[fieldKey.value]) // 当前展开状态

  const keys = state
    ? newExpandedKeys.value.filter((k) => k !== data[fieldKey.value]) // 已展开
    : [...newExpandedKeys.value, data[fieldKey.value]] // 未展开

  newExpandedKeys.value = keys

  onExpand(keys, { expanded: !state, node: data })
  emits('nodeClick', data)
}

// ———————————————————————————导出方法———————————————————————— //

// 展开所有
const expandAll = () => {
  newExpandedKeys.value = getAllNodeKeys() // 使用Set去重
  onExpand(newExpandedKeys.value)
}

// 收起所有
const collapseAll = () => {
  newExpandedKeys.value = []
  onExpand(newExpandedKeys.value)
}

// 展开指定根目录
const expandRoot = (key: Key) => {
  if (!props.treeData?.length) return

  const rootNode = props.treeData?.find((item) => item[fieldKey.value] === key)

  if (rootNode && !newExpandedKeys.value.includes(key)) {
    newExpandedKeys.value = [...newExpandedKeys.value, key]
  }
  onExpand(newExpandedKeys.value)
}

// 收起指定根目录
const collapseRoot = (key: Key) => {
  if (newExpandedKeys.value.includes(key)) {
    newExpandedKeys.value = newExpandedKeys.value.filter((k) => k !== key)
  }
  onExpand(newExpandedKeys.value)
}

// 展开所有根目录
const expandAllRoots = () => {
  if (!props.treeData?.length) return

  const rootKeys = props.treeData
    .map((item) => item[fieldKey.value])
    .filter(Boolean) as (string | number)[]

  // 使用 Set 去重后合并
  const mergedKeys = [...new Set([...newExpandedKeys.value, ...rootKeys])]
  newExpandedKeys.value = mergedKeys
  onExpand(newExpandedKeys.value)
}

// 收起所有根目录
const collapseAllRoots = () => {
  if (!props.treeData?.length) {
    newExpandedKeys.value = []
    return
  }

  const rootKeys = props.treeData
    .map((item) => item[fieldKey.value])
    .filter(Boolean) as (string | number)[]

  // 过滤掉所有根节点的 key
  newExpandedKeys.value = newExpandedKeys.value.filter(
    (key) => !rootKeys.includes(key)
  )
  onExpand(newExpandedKeys.value)
}

// 选中指定节点
const selection = (key: Key) => {
  if (props.multiple) {
    // 多选模式：如果 key 不存在则添加
    if (!newSelectedKeys.value.includes(key)) {
      newSelectedKeys.value = [...newSelectedKeys.value, key]
    }
  } else {
    // 单选模式：直接替换为当前 key（覆盖旧值）
    newSelectedKeys.value = [key]
  }
  onSelect(newSelectedKeys.value)
}

// 取消选中指定节点
const clearSelection = (key: Key) => {
  if (newSelectedKeys.value.includes(key)) {
    newSelectedKeys.value = newSelectedKeys.value.filter((k) => k !== key)
  }
  onSelect(newSelectedKeys.value)
}

// 全选
const checkedAll = () => {
  if (!props.treeData?.length) return

  if (props.checkStrictly) {
    newCheckedKeys.value = {
      checked: getAllNodeKeys(),
      halfChecked: [],
    }
  } else {
    newCheckedKeys.value = getAllNodeKeys()
  }
  onCheck(newCheckedKeys.value)
}

// 取消全选
const deselectAll = () => {
  if (props.checkStrictly) {
    newCheckedKeys.value = {
      checked: [],
      halfChecked: [],
    }
  } else {
    newCheckedKeys.value = []
  }
  onCheck(newCheckedKeys.value)
}

// 获取所有节点的key
const getAllNodeKeys = (): Key[] => {
  if (!props.treeData?.length) return []

  const allKeys = [] as Key[]

  const collectKeys = (nodes: any[]) => {
    nodes.forEach((node) => {
      if (node[fieldKey.value] != null) {
        allKeys.push(node[fieldKey.value])
      }
      if (node[fieldChildren.value]?.length) {
        collectKeys(node[fieldChildren.value])
      }
    })
  }

  collectKeys(props.treeData)
  return [...new Set(allKeys)]
}

// 查找指定节点（辅助函数）
const findNode = (key: Key): DataNode | null => {
  const find = (nodes: DataNode[]): DataNode | null => {
    for (const node of nodes) {
      if (node[fieldKey.value] === key) return node
      if (node[fieldChildren.value]?.length) {
        const found = find(node[fieldChildren.value])
        if (found) return found
      }
    }
    return null
  }
  return find(props.treeData || [])
}

defineExpose({
  expandAll, // 展开所有
  collapseAll, // 收起所有
  expandRoot, // 展开指定根目录
  collapseRoot, // 收起指定根目录
  expandAllRoots, // 展开所有根目录
  collapseAllRoots, // 收起所有根目录
  selection, // 选中指定节点
  clearSelection, // 取消选中指定节点
  checkedAll, // 全选
  deselectAll, // 取消全选
  getAllNodeKeys, //获取所有节点的key
  findNode, // 查找指定节点（辅助函数）
})
</script>
